import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Volume2, VolumeX, X, Clock, AlertTriangle, CheckCircle, Eye } from 'lucide-react';
import useAudioAlerts from '../hooks/useAudioAlerts';
import { auditsApi } from '../services/api';

interface TimeAlert {
  id: number;
  auditId: number;
  tipoAlerta: 'TIEMPO_MENOR' | 'TIEMPO_MAYOR';
  tiempoEstimado: number;
  tiempoReal: number;
  porcentajeReal: number;
  mensaje: string;
  estado: 'pendiente' | 'revisada' | 'resolvida';
  prioridad: 'baja' | 'media' | 'alta';
  createdAt: string;
  operadorId: number;
  clientId: number;
  metadatos?: {
    operadorNombre?: string;
    clienteNombre?: string;
    sucursalNombre?: string;
  };
  // Relaciones desde el backend
  audit?: {
    id: number;
    titulo: string;
    estado: string;
    client?: {
      id: number;
      nombre: string;
    };
    branch?: {
      id: number;
      nombre: string;
    };
  };
  operadorAlert?: {
    id: number;
    name: string;
    username: string;
  };
}

interface TimeAlertNotificationsProps {
  user: any;
  onAlertClick?: (alert: TimeAlert) => void;
}

const TimeAlertNotifications: React.FC<TimeAlertNotificationsProps> = ({
  user,
  onAlertClick
}) => {
  const [timeAlerts, setTimeAlerts] = useState<TimeAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showHistorial, setShowHistorial] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState<string>('all');
  const [filtroOperador, setFiltroOperador] = useState<string>('');
  const [filtroMes, setFiltroMes] = useState<string>('');
  const [operadores, setOperadores] = useState<{id: number, name: string}[]>([]);

  // Generar opciones de meses (últimos 12 meses)
  const generarOpcionesMeses = () => {
    const meses = [];
    const ahora = new Date();
    for (let i = 0; i < 12; i++) {
      const fecha = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
      const valor = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
      const etiqueta = fecha.toLocaleDateString('es-AR', { year: 'numeric', month: 'long' });
      meses.push({ valor, etiqueta });
    }
    return meses;
  };

  // Filtrar alertas por mes seleccionado
  const alertasFiltradas = filtroMes
    ? timeAlerts.filter(alert => {
        const fechaAlerta = new Date(alert.createdAt);
        const mesAlerta = `${fechaAlerta.getFullYear()}-${String(fechaAlerta.getMonth() + 1).padStart(2, '0')}`;
        return mesAlerta === filtroMes;
      })
    : timeAlerts;

  // Guardar lastCheck en localStorage para que persista entre recargas
  const [lastCheck, setLastCheck] = useState<Date>(() => {
    const saved = localStorage.getItem('timeAlertsLastCheck');
    return saved ? new Date(saved) : new Date();
  });

  // Rastrear IDs de alertas que ya fueron notificadas (sonido + notificación)
  const [notifiedAlertIds, setNotifiedAlertIds] = useState<Set<number>>(() => {
    const saved = localStorage.getItem('notifiedAlertIds');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // Guardar lastCheck en localStorage
  useEffect(() => {
    localStorage.setItem('timeAlertsLastCheck', lastCheck.toISOString());
  }, [lastCheck]);

  // Guardar en localStorage cada vez que cambie el Set de alertas notificadas
  useEffect(() => {
    localStorage.setItem('notifiedAlertIds', JSON.stringify([...notifiedAlertIds]));
  }, [notifiedAlertIds]);

  const {
    alerts: audioAlerts,
    isMuted,
    soundEnabled,
    addTimeAlert,
    addSystemAlert,
    removeAlert,
    clearAllAlerts,
    toggleMute,
    toggleSound,
    requestNotificationPermission
  } = useAudioAlerts({
    enableSound: true,
    soundVolume: 0.6,
    enableNotifications: true
  });

  // Cargar alertas al montar el componente
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'supervisor') {
      loadTimeAlerts();
      loadOperadores();
      requestNotificationPermission();

      // Polling cada 30 segundos para nuevas alertas
      const interval = setInterval(() => {
        checkForNewAlerts();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [user]);

  // Recargar historial cuando cambian los filtros
  useEffect(() => {
    if (showHistorial) {
      loadHistorial();
    }
  }, [filtroEstado, filtroOperador, showHistorial]);

  const loadTimeAlerts = async () => {
    try {
      setLoading(true);
      const response = await auditsApi.getTimeAlerts();
      if (response.data.success) {
        const alerts = response.data.data;
        setTimeAlerts(alerts);

        // Marcar todas las alertas cargadas como ya notificadas (sin reproducir sonido)
        if (alerts.length > 0) {
          setNotifiedAlertIds(prev => {
            const newSet = new Set(prev);
            alerts.forEach((alert: TimeAlert) => newSet.add(alert.id));
            return newSet;
          });
        }
      }
    } catch (error: any) {
      console.error('Error cargando alertas de tiempo:', error);
    } finally {
      setLoading(false);
    }
  };

  // Cargar historial con filtros
  const loadHistorial = async () => {
    try {
      setLoading(true);
      const params: any = { todos: 'true', limit: 500 };
      if (filtroEstado && filtroEstado !== 'all') params.estado = filtroEstado;
      if (filtroOperador) params.operadorId = filtroOperador;

      const response = await auditsApi.getTimeAlerts(params);
      if (response.data.success) {
        setTimeAlerts(response.data.data);
      }
    } catch (error: any) {
      console.error('Error cargando historial:', error);
    } finally {
      setLoading(false);
    }
  };

  // Imprimir historial agrupado por mes
  const imprimirHistorial = () => {
    // Agrupar alertas filtradas por mes y colaborador
    const agrupado: { [mes: string]: { [colaborador: string]: { exceso: number; menor: number; alertas: any[] } } } = {};

    alertasFiltradas.forEach(alert => {
      const fecha = new Date(alert.createdAt);
      const mes = fecha.toLocaleDateString('es-AR', { year: 'numeric', month: 'long' });
      const colaborador = alert.operadorAlert?.name || alert.metadatos?.operadorNombre || 'Sin asignar';

      if (!agrupado[mes]) agrupado[mes] = {};
      if (!agrupado[mes][colaborador]) agrupado[mes][colaborador] = { exceso: 0, menor: 0, alertas: [] };

      if (alert.tipoAlerta === 'TIEMPO_MAYOR') {
        agrupado[mes][colaborador].exceso++;
      } else {
        agrupado[mes][colaborador].menor++;
      }
      agrupado[mes][colaborador].alertas.push(alert);
    });

    // Generar HTML para imprimir
    const colaboradorNombre = filtroOperador
      ? operadores.find(o => o.id === parseInt(filtroOperador))?.name || 'Colaborador'
      : 'Todos los colaboradores';

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Historial de Alertas</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1f2937; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 30px; background: #f3f4f6; padding: 10px; border-radius: 5px; }
          h3 { color: #4b5563; margin-left: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0 20px 20px; }
          th, td { border: 1px solid #e5e7eb; padding: 8px; text-align: left; }
          th { background: #f9fafb; }
          .exceso { color: #dc2626; }
          .menor { color: #f59e0b; }
          .resumen { background: #dbeafe; padding: 10px; border-radius: 5px; margin: 10px 0; }
          .total { font-weight: bold; font-size: 1.1em; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>📊 Historial de Alertas de Tiempo</h1>
        <p><strong>Colaborador:</strong> ${colaboradorNombre}</p>
        <p><strong>Mes:</strong> ${filtroMes ? generarOpcionesMeses().find(m => m.valor === filtroMes)?.etiqueta || filtroMes : 'Todos los meses'}</p>
        <p><strong>Fecha de impresión:</strong> ${new Date().toLocaleString('es-AR')}</p>
        <p><strong>Total de alertas:</strong> ${alertasFiltradas.length}</p>
    `;

    Object.entries(agrupado).forEach(([mes, colaboradores]) => {
      html += `<h2>📅 ${mes.toUpperCase()}</h2>`;

      Object.entries(colaboradores).forEach(([colaborador, datos]) => {
        html += `
          <h3>👤 ${colaborador}</h3>
          <div class="resumen">
            <span class="exceso">Exceso de tiempo: ${datos.exceso}</span> |
            <span class="menor">Menos de tiempo: ${datos.menor}</span> |
            <span class="total">Total: ${datos.exceso + datos.menor}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Cliente</th>
                <th>Auditoría</th>
                <th>Estimado</th>
                <th>Real</th>
                <th>Diferencia</th>
              </tr>
            </thead>
            <tbody>
        `;

        datos.alertas.forEach(alert => {
          const fechaAlerta = new Date(alert.createdAt).toLocaleDateString('es-AR');
          const tipo = alert.tipoAlerta === 'TIEMPO_MAYOR' ? 'Exceso' : 'Menor';
          const tipoClass = alert.tipoAlerta === 'TIEMPO_MAYOR' ? 'exceso' : 'menor';
          const cliente = alert.audit?.client?.nombre || alert.metadatos?.clienteNombre || '-';
          const auditoria = alert.audit?.titulo || '-';
          const estimado = Math.floor(alert.tiempoEstimado / 60) + 'h ' + (alert.tiempoEstimado % 60) + 'm';
          const real = Math.floor(alert.tiempoReal / 60) + 'h ' + (alert.tiempoReal % 60) + 'm';
          const diff = Math.abs(alert.tiempoReal - alert.tiempoEstimado) + ' min';

          html += `
            <tr>
              <td>${fechaAlerta}</td>
              <td class="${tipoClass}">${tipo}</td>
              <td>${cliente}</td>
              <td>${auditoria}</td>
              <td>${estimado}</td>
              <td>${real}</td>
              <td>${diff}</td>
            </tr>
          `;
        });

        html += `</tbody></table>`;
      });
    });

    html += `</body></html>`;

    // Abrir ventana de impresión
    const ventana = window.open('', '_blank');
    if (ventana) {
      ventana.document.write(html);
      ventana.document.close();
      ventana.print();
    }
  };

  // Cargar operadores y supervisores para el filtro
  const loadOperadores = async () => {
    try {
      // Cargar operadores
      const respOps = await fetch('/api/users/operators', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const dataOps = await respOps.json();

      // Cargar supervisores
      const respSups = await fetch('/api/users?role=supervisor', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const dataSups = await respSups.json();

      const ops = dataOps.data || [];
      const sups = (dataSups.data || []).map((s: any) => ({ id: s.id, name: s.name + ' (Sup)' }));

      // Combinar y ordenar por nombre
      const todos = [...ops, ...sups].sort((a, b) => a.name.localeCompare(b.name));
      setOperadores(todos);
    } catch (error) {
      console.error('Error cargando colaboradores:', error);
    }
  };

  const checkForNewAlerts = async () => {
    try {
      const response = await auditsApi.getTimeAlerts({ since: lastCheck.toISOString() });
      if (response.data.success && response.data.data.length > 0) {
        const newAlerts = response.data.data;

        console.log(`[TimeAlerts Frontend] Recibidas ${newAlerts.length} alertas desde ${lastCheck.toISOString()}`);

        // Filtrar solo las alertas que NO han sido notificadas (nuevas)
        const alertsToNotify = newAlerts.filter((alert: TimeAlert) => !notifiedAlertIds.has(alert.id));

        console.log(`[TimeAlerts Frontend] Alertas nuevas a notificar: ${alertsToNotify.length}`);

        // Agregar SOLO las alertas nuevas al estado (evitar duplicados)
        if (alertsToNotify.length > 0) {
          // Evitar duplicados también en el estado
          setTimeAlerts(prev => {
            const existingIds = new Set(prev.map(a => a.id));
            const uniqueNew = alertsToNotify.filter(alert => !existingIds.has(alert.id));
            return [...uniqueNew, ...prev];
          });

          // Reproducir sonido y mostrar notificación para cada nueva alerta
          alertsToNotify.forEach((alert: TimeAlert) => {
            const severity = alert.prioridad === 'alta' ? 'high' :
                            alert.prioridad === 'media' ? 'medium' : 'low';

            const tipoTexto = alert.tipoAlerta === 'TIEMPO_MAYOR' ? 'EXCESO' : 'ANTICIPO';
            const clienteNombre = alert.metadatos?.clienteNombre || 'Cliente';
            const operadorNombre = alert.metadatos?.operadorNombre || 'Operador';

            addTimeAlert(
              `🚨 ${tipoTexto} DE TIEMPO: ${operadorNombre} - ${clienteNombre} (${Math.abs(alert.tiempoReal - alert.tiempoEstimado)}min)`,
              severity
            );
          });

          // Agregar los IDs de las alertas notificadas al Set
          setNotifiedAlertIds(prev => {
            const newSet = new Set(prev);
            alertsToNotify.forEach((alert: TimeAlert) => newSet.add(alert.id));
            return newSet;
          });
        }

        // Actualizar lastCheck SIEMPRE, incluso si no hay alertas nuevas
        setLastCheck(new Date());
      } else {
        // Si no hay alertas, también actualizar lastCheck para evitar consultar el mismo rango
        setLastCheck(new Date());
      }
    } catch (error) {
      console.error('Error verificando nuevas alertas:', error);
    }
  };

  const markAsViewed = async (alertId: number) => {
    try {
      await auditsApi.markTimeAlertAsViewed(alertId);
      setTimeAlerts(prev =>
        prev.map(alert =>
          alert.id === alertId
            ? { ...alert, estado: 'revisada' as const }
            : alert
        )
      );
    } catch (error) {
      console.error('Error marcando alerta como vista:', error);
    }
  };

  const resolveAlert = async (alertId: number, resolution?: string) => {
    try {
      await auditsApi.resolveTimeAlert(alertId, resolution);
      setTimeAlerts(prev =>
        prev.map(alert =>
          alert.id === alertId
            ? { ...alert, estado: 'resuelta' as const }
            : alert
        )
      );
      addSystemAlert('Alerta resuelta exitosamente', 'low');
    } catch (error) {
      console.error('Error resolviendo alerta:', error);
    }
  };

  const getPendingAlertsCount = () => {
    return timeAlerts.filter(alert => alert.estado === 'pendiente').length;
  };

  const getAlertIcon = (alert: TimeAlert) => {
    if (alert.tipoAlerta === 'TIEMPO_MAYOR') {
      return <AlertTriangle style={{ width: '1rem', height: '1rem', color: '#dc2626' }} />;
    }
    return <Clock style={{ width: '1rem', height: '1rem', color: '#f59e0b' }} />;
  };

  const getAlertColor = (alert: TimeAlert) => {
    if (alert.estado === 'resuelta') return '#10b981';
    if (alert.estado === 'revisada') return '#6b7280';
    if (alert.prioridad === 'alta') return '#dc2626';
    if (alert.prioridad === 'media') return '#f59e0b';
    return '#3b82f6';
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (user?.role !== 'admin' && user?.role !== 'supervisor') {
    return null;
  }

  return (
    <>
      {/* Botón de notificaciones */}
      <div style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1000 }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {/* Controles de sonido */}
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button
              onClick={toggleSound}
              style={{
                padding: '0.5rem',
                backgroundColor: soundEnabled ? '#3b82f6' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
              title={soundEnabled ? 'Desactivar sonido' : 'Activar sonido'}
            >
              {soundEnabled ?
                <Volume2 style={{ width: '1rem', height: '1rem' }} /> :
                <VolumeX style={{ width: '1rem', height: '1rem' }} />
              }
            </button>

            <button
              onClick={toggleMute}
              style={{
                padding: '0.5rem',
                backgroundColor: isMuted ? '#dc2626' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
              title={isMuted ? 'Activar alertas' : 'Silenciar alertas'}
            >
              {isMuted ?
                <BellOff style={{ width: '1rem', height: '1rem' }} /> :
                <Bell style={{ width: '1rem', height: '1rem' }} />
              }
            </button>
          </div>

          {/* Botón principal de alertas */}
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              padding: '0.75rem',
              backgroundColor: getPendingAlertsCount() > 0 ? '#dc2626' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              position: 'relative',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Bell style={{ width: '1.25rem', height: '1.25rem' }} />
            {getPendingAlertsCount() > 0 && (
              <>
                <span style={{ fontWeight: 'bold' }}>
                  {getPendingAlertsCount()}
                </span>
                <div style={{
                  position: 'absolute',
                  top: '-0.25rem',
                  right: '-0.25rem',
                  width: '0.5rem',
                  height: '0.5rem',
                  backgroundColor: '#fbbf24',
                  borderRadius: '50%',
                  animation: 'pulse 2s infinite'
                }} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Panel de alertas */}
      {showNotifications && (
        <div style={{
          position: 'fixed',
          top: '5rem',
          right: '1rem',
          width: '400px',
          maxHeight: '70vh',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.75rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '1.125rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Clock style={{ width: '1.25rem', height: '1.25rem', color: '#3b82f6' }} />
              Alertas de Tiempo
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                onClick={() => setShowHistorial(!showHistorial)}
                style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: showHistorial ? '#3b82f6' : '#e5e7eb',
                  color: showHistorial ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: '0.25rem',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                {showHistorial ? '← Volver' : '📊 Historial'}
              </button>
              {timeAlerts.length > 0 && !showHistorial && (
                <button
                  onClick={() => {
                    setTimeAlerts([]);
                    setNotifiedAlertIds(new Set());
                  }}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    cursor: 'pointer'
                  }}
                  title="Limpiar lista y permitir que las alertas vuelvan a sonar"
                >
                  Limpiar
                </button>
              )}
              <button
                onClick={() => setShowNotifications(false)}
                style={{
                  padding: '0.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <X style={{ width: '1rem', height: '1rem', color: '#6b7280' }} />
              </button>
            </div>
          </div>

          {/* Filtros de historial */}
          {showHistorial && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: '#f0f9ff',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <select
                value={filtroOperador}
                onChange={(e) => setFiltroOperador(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem',
                  flex: 1
                }}
              >
                <option value="">Todos los colaboradores</option>
                {operadores.map(op => (
                  <option key={op.id} value={op.id}>{op.name}</option>
                ))}
              </select>
              <select
                value={filtroMes}
                onChange={(e) => setFiltroMes(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">Todos los meses</option>
                {generarOpcionesMeses().map(mes => (
                  <option key={mes.valor} value={mes.valor}>{mes.etiqueta}</option>
                ))}
              </select>
              <select
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem'
                }}
              >
                <option value="all">Todos los estados</option>
                <option value="pendiente">Pendientes</option>
                <option value="revisada">Revisadas</option>
                <option value="resuelta">Resueltas</option>
              </select>
              <button
                onClick={imprimirHistorial}
                disabled={alertasFiltradas.length === 0}
                style={{
                  padding: '0.5rem 0.75rem',
                  backgroundColor: alertasFiltradas.length === 0 ? '#9ca3af' : '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  cursor: alertasFiltradas.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
                title="Imprimir historial"
              >
                🖨️ Imprimir
              </button>
            </div>
          )}

          {/* Lista de alertas */}
          <div style={{
            maxHeight: showHistorial ? '350px' : '400px',
            overflowY: 'auto',
            padding: '0.5rem'
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                Cargando alertas...
              </div>
            ) : (showHistorial ? alertasFiltradas : timeAlerts).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                {showHistorial ? 'No hay alertas con estos filtros' : 'No hay alertas de tiempo'}
              </div>
            ) : (
              (showHistorial ? alertasFiltradas : timeAlerts).map(alert => (
                <div
                  key={alert.id}
                  style={{
                    padding: '0.75rem',
                    margin: '0.25rem',
                    backgroundColor: alert.estado === 'pendiente' ? '#fef3c7' : '#f3f4f6',
                    border: `1px solid ${getAlertColor(alert)}`,
                    borderRadius: '0.5rem',
                    cursor: 'pointer'
                  }}
                  onClick={() => onAlertClick?.(alert)}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {getAlertIcon(alert)}
                      <span style={{
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        color: getAlertColor(alert)
                      }}>
                        {alert.tipoAlerta === 'TIEMPO_MAYOR' ? 'EXCESO' : 'ANTICIPO'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      {alert.estado === 'pendiente' && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsViewed(alert.id);
                            }}
                            style={{
                              padding: '0.25rem',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer'
                            }}
                            title="Marcar como vista"
                          >
                            <Eye style={{ width: '0.75rem', height: '0.75rem' }} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              resolveAlert(alert.id);
                            }}
                            style={{
                              padding: '0.25rem',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              cursor: 'pointer'
                            }}
                            title="Resolver"
                          >
                            <CheckCircle style={{ width: '0.75rem', height: '0.75rem' }} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                      👤 {alert.operadorAlert?.name || alert.metadatos?.operadorNombre || 'Operador'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>
                      🏢 {alert.audit?.client?.nombre || alert.metadatos?.clienteNombre || 'Cliente'}
                      {alert.audit?.branch?.nombre && ` - ${alert.audit.branch.nombre}`}
                    </div>
                    {alert.audit?.titulo && (
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        📋 {alert.audit.titulo}
                      </div>
                    )}
                  </div>

                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    padding: '0.5rem',
                    borderRadius: '0.25rem',
                    marginBottom: '0.5rem'
                  }}>
                    <div>⏱️ Estimado: {formatTime(alert.tiempoEstimado)} | Real: {formatTime(alert.tiempoReal)}</div>
                    <div style={{ marginTop: '0.25rem' }}>
                      📊 Diferencia: {Math.abs(alert.tiempoReal - alert.tiempoEstimado)}min
                      ({alert.porcentajeReal ? `${alert.porcentajeReal}%` : 'N/A'})
                    </div>
                  </div>

                  {alert.auditId && (
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#3b82f6',
                      fontWeight: '500',
                      marginBottom: '0.25rem'
                    }}>
                      🔗 Haz clic para ver la auditoría #{alert.auditId}
                    </div>
                  )}

                  <div style={{
                    fontSize: '0.7rem',
                    color: '#9ca3af',
                    marginTop: '0.25rem'
                  }}>
                    📅 {new Date(alert.createdAt).toLocaleString('es-AR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                      timeZone: 'America/Argentina/Buenos_Aires'
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Alertas flotantes (audio alerts) */}
      {audioAlerts.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: '1rem',
          right: '1rem',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}>
          {audioAlerts.slice(0, 3).map(alert => (
            <div
              key={alert.id}
              style={{
                padding: '1rem',
                backgroundColor: alert.severity === 'high' ? '#dc2626' :
                               alert.severity === 'medium' ? '#f59e0b' : '#3b82f6',
                color: 'white',
                borderRadius: '0.5rem',
                maxWidth: '350px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                animation: 'slideInRight 0.3s ease-out'
              }}
            >
              <div style={{ fontSize: '0.875rem' }}>
                {alert.message}
              </div>
              <button
                onClick={() => removeAlert(alert.id)}
                style={{
                  padding: '0.25rem',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                <X style={{ width: '1rem', height: '1rem' }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* CSS para animaciones */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default TimeAlertNotifications;